// Capability gating for the merge-semantics edits (P3 #23): given a process
// WalkNode, decide which merge / inclusive-count / await-target affordances a
// surface offers on it. Pure over the walk tree so every surface offers the SAME
// items, and coarse-by-design — the walk erases the structured merge shape
// (policy / count / boolean threshold), exposing only a rendered `mergeAspect`
// string, so this offers the affordance and the ENGINE is the exact guard
// (`setMergeClause` refuses a `parallel await …` and a count over a named-branch
// merge, surfacing the reason through the shared onReject toast). Mirrors
// processBlockMorph.ts — outside StepEditMenu.tsx (a component file) so it imports
// without a Fast-Refresh incompatible export and unit-tests directly.

import type { WalkNode } from "@archlang/engine";

/** The merge-behaviour affordance for a `parallel` node. */
export interface MergeCaps {
  /** The merge keyword+threshold can be set (a plain `parallel`, NOT an await
   *  boundary — await already implies race and can't carry join/race). */
  readonly canSetMerge: boolean;
  /** The currently-rendered merge threshold (`join 2`, `race`, `out`, …) —
   *  prefills the count prompt / shows the active preset. Undefined = bare. */
  readonly current?: string;
}

/** The inclusive-choice count affordance (`select`/`parallel select`). */
export interface SelectCountCaps {
  /** A count can be set on this node's inclusive choice. */
  readonly canSetCount: boolean;
  /** The `parallel select` form routes through `setParallelSelectCount`; a
   *  top-level `switch` (=`select`) through `setSelectCount`. */
  readonly parallel: boolean;
  /** Cases run at runtime — undefined = all (bare), 1 = one, N = N. Prefills. */
  readonly runCount?: number;
}

/** True when a `parallel` walk node is the `await` boundary form (its rendered
 *  merge aspect is the await head). Such a node takes no join/race merge. */
function isAwaitParallel(node: Extract<WalkNode, { kind: "parallel" }>): boolean {
  return node.mergeAspect?.startsWith("await ") ?? false;
}

/** True when a `parallel` walk node is the concurrent-inclusive `select` form
 *  (`parallel select [N]`) — its merge aspect renders as `select …`. */
function isSelectParallel(node: Extract<WalkNode, { kind: "parallel" }>): boolean {
  return node.mergeAspect === "select" || (node.mergeAspect?.startsWith("select ") ?? false);
}

/** Merge-behaviour capabilities of a walk node — the `parallel` merge keyword +
 *  threshold. NONE for every non-parallel / await-boundary / select-form node. */
export function mergeCapsFor(node: WalkNode): MergeCaps {
  if (node.kind !== "parallel") return { canSetMerge: false };
  // The await boundary and the `parallel select` form don't carry a join/race
  // merge — the select form is a count, the await form implies race.
  if (isAwaitParallel(node) || isSelectParallel(node)) return { canSetMerge: false, current: node.mergeAspect };
  return { canSetMerge: true, current: node.mergeAspect };
}

/** Inclusive-count capabilities — a top-level `select` (`switch`) or the
 *  `parallel select` form. NONE for everything else. */
export function selectCountCapsFor(node: WalkNode): SelectCountCaps {
  if (node.kind === "switch") return { canSetCount: true, parallel: false, runCount: node.runCount };
  if (node.kind === "parallel" && isSelectParallel(node)) {
    // The rendered aspect is `select` (all) or `select N`; parse the N for the
    // prefill (`one` never renders — it prints as `select 1` upstream? no: the
    // walker emits `select 1` for one and `select` for all; a bare `select` = all).
    const m = /^select (\d+)$/.exec(node.mergeAspect ?? "");
    return { canSetCount: true, parallel: true, runCount: m ? Number(m[1]) : undefined };
  }
  return { canSetCount: false, parallel: false };
}

/** True when a walk node is a `parallel await …` boundary — its head is
 *  await-target-editable (`setAwaitTarget` on the parallel node). */
export function isAwaitBoundaryParallel(node: WalkNode): boolean {
  return node.kind === "parallel" && isAwaitParallel(node);
}
